import { useTranslation } from 'react-i18next';

import { PATTERN_IDS, type Pattern, patternLayer, patternOf } from '@/entities/appearance';
import { ColorSwatches } from '@/shared/ui';

import styles from './editor.module.css';

interface Props {
  value: Pattern;
  onChange: (value: Pattern) => void;
}

/** A preset pattern id with its colors, opacity and scale in fixed bounds. */
export function PatternEditor({ value, onChange }: Props) {
  const { t } = useTranslation();
  return (
    <div className={styles.section}>
      <p className={styles.sectionTitle}>{t('appearance.pattern.kind')}</p>
      <div className={styles.options} role="radiogroup" aria-label={t('appearance.pattern.kind')}>
        {PATTERN_IDS.map((id) => {
          const layer = patternLayer({ ...patternOf(id, value.color), opacity: 0.5 });
          return (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={value.id === id}
              className={styles.option}
              onClick={() => {
                onChange({ ...value, id });
              }}
            >
              <span
                className={styles.optionSample}
                style={
                  layer ? { backgroundImage: layer.image, backgroundSize: layer.size } : undefined
                }
              />
              <strong>{t(`appearance.pattern.ids.${id}`)}</strong>
            </button>
          );
        })}
      </div>
      <ColorSwatches
        label={t('appearance.pattern.color')}
        value={value.color}
        onChange={(color) => {
          if (color) onChange({ ...value, color });
        }}
      />
      <ColorSwatches
        label={t('appearance.pattern.background')}
        value={value.background}
        noneLabel={t('appearance.pattern.noBackground')}
        onChange={(background) => {
          onChange({ ...value, background });
        }}
      />
      <div className={styles.row}>
        <label className={styles.slider}>
          <span>
            {t('appearance.pattern.opacity', { percent: Math.round(value.opacity * 100) })}
          </span>
          <input
            type="range"
            min={5}
            max={60}
            step={5}
            value={Math.round(value.opacity * 100)}
            onChange={(event) => {
              onChange({ ...value, opacity: Number(event.target.value) / 100 });
            }}
          />
        </label>
        <label className={styles.slider}>
          <span>{t('appearance.pattern.scale', { scale: value.scale })}</span>
          <input
            type="range"
            min={1}
            max={3}
            step={1}
            value={value.scale}
            onChange={(event) => {
              onChange({ ...value, scale: Number(event.target.value) });
            }}
          />
        </label>
      </div>
    </div>
  );
}
